import { useEffect, useRef, useState } from 'react';
import type { BrandingSettings } from '@orion/contracts';
import { brandingSettingsSchema } from '@orion/contracts';
import { Button, Switch } from '@orion/ui';
import { PageHeader } from '../../../app/shell.js';
import {
  useBrandingSettingsQuery,
  useRemoveFavicon,
  useRemoveLogo,
  useRemoveLogoDark,
  useUploadFavicon,
  useUploadLogo,
  useUploadLogoDark,
} from '../../branding/api.js';
import { useUpdateSettings } from './api.js';

const DEFAULT_BRANDING = brandingSettingsSchema.parse({});

// design/orion-crm-design/rendered-html/AdminBranding.html freezes mid-edit
// (2 pending changes) — reproduced by replaying those exact 2 edits against
// the real dirty-tracking state, same approach as the other Phase 4 admin
// screens.
const isDesignBrandingPreview = import.meta.env.DEV && new URLSearchParams(window.location.search).get('__design') === 'AdminBranding';

// Curated so every swatch keeps white foreground text at AA contrast on top
// of it (design/orion-crm-design/rendered-html/AdminBranding.html) — an
// open colour picker can't make that promise, so the design's 6-swatch
// palette is the actual constraint, not just its starting suggestion.
const ACCENT_PRESETS: Array<{ label: string; hex: string }> = [
  { label: 'Orion blue', hex: '#2E47D6' },
  { label: 'Teal', hex: '#0F6B63' },
  { label: 'Violet', hex: '#5B34B0' },
  { label: 'Green', hex: '#1E7A4C' },
  { label: 'Amber', hex: '#B4610F' },
  { label: 'Slate', hex: '#454A54' },
];

/** WCAG 2.x relative-luminance contrast ratio between white text and `hex`
 * — real arithmetic (docs/architecture/craft.md accessibility rules), not a
 * hardcoded "Passes AA" label that would silently lie the moment someone
 * picks a colour outside the curated presets. */
function contrastWithWhite(hex: string): number {
  const linear = (channel: number) => (channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4);
  const r = linear(parseInt(hex.slice(1, 3), 16) / 255);
  const g = linear(parseInt(hex.slice(3, 5), 16) / 255);
  const b = linear(parseInt(hex.slice(5, 7), 16) / 255);
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return (1 + 0.05) / (luminance + 0.05);
}

function LogoSlot({
  label,
  preview,
  dark,
  onUpload,
  onRemove,
  uploading,
}: {
  label: string;
  preview: string | null;
  dark: boolean;
  onUpload: (file: File) => void;
  onRemove: () => void;
  uploading: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[13px] font-semibold">{label}</span>
      <div className={`flex h-16 items-center justify-center gap-2 rounded-control border ${dark ? 'border-transparent bg-night' : 'border-border bg-surface-subtle'}`}>
        {preview ? (
          <img src={preview} alt="" className="h-10 max-w-[80%] object-contain" />
        ) : (
          <>
            <span className="flex size-6 items-center justify-center rounded-[6px]" style={{ background: dark ? '#1f2430' : '#12151b' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="6.5" cy="16" r="2" fill="#F2B25C" />
                <circle cx="12" cy="12" r="2" fill="#F2B25C" />
                <circle cx="17.5" cy="8" r="2" fill="#F2B25C" />
                <circle cx="5" cy="5" r="1.1" fill="#8A909C" />
                <circle cx="19" cy="19" r="1.1" fill="#8A909C" />
              </svg>
            </span>
            <span className={`text-base font-[650] tracking-[-0.01em] ${dark ? 'text-white' : 'text-ink'}`}>Orion</span>
          </>
        )}
      </div>
      <span className="flex gap-2.5 text-xs">
        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])} />
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} className="font-semibold text-primary">
          Replace
        </button>
        {preview && (
          <button type="button" onClick={onRemove} className="font-semibold text-text-muted">
            Remove
          </button>
        )}
      </span>
    </div>
  );
}

/** Admin ▸ Branding — a bespoke page rather than the generic `SettingsForm`
 * since a logo upload and a colour swatch don't fit its flat-field model,
 * and both need to be visible against a live preview so an admin can tell
 * what they're about to ship before saving (docs section 5 "Administrator
 * Control"). Saved values are applied app-wide at runtime by the sidebar and
 * login screen reading `/public/branding`, not just stored inertly. */
export function BrandingSettingsPage() {
  const { data, isLoading } = useBrandingSettingsQuery();
  const update = useUpdateSettings<BrandingSettings>('branding');
  const uploadLogo = useUploadLogo();
  const removeLogo = useRemoveLogo();
  const uploadLogoDark = useUploadLogoDark();
  const removeLogoDark = useRemoveLogoDark();
  const uploadFavicon = useUploadFavicon();
  const removeFavicon = useRemoveFavicon();

  const [draft, setDraft] = useState<BrandingSettings | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (data && draft === null) {
      setDraft(isDesignBrandingPreview ? { ...data, loginHeadline: 'Welcome to OES Workspace', supportEmail: 'crm-support@oes.com' } : data);
    }
  }, [data, draft]);

  const values = draft;
  const original = data;

  const changedKeys = new Set(
    values && original
      ? (Object.keys(values) as Array<keyof BrandingSettings>).filter((k) => JSON.stringify(values[k]) !== JSON.stringify(original[k]))
      : [],
  );
  const FIELD_LABEL: Partial<Record<keyof BrandingSettings, string>> = {
    productName: 'Product name',
    workspaceName: 'Workspace name',
    accentColor: 'Accent colour',
    loginHeadline: 'Login page headline',
    loginSubtext: 'Login page subtext',
    supportEmail: 'Support email',
    showPoweredBy: 'Show "Powered by"',
  };
  const dirty = changedKeys.size > 0;

  const setField = <K extends keyof BrandingSettings>(key: K, value: BrandingSettings[K]) => {
    setDraft((d) => (d ? { ...d, [key]: value } : d));
    setSaveError(null);
  };

  const save = async () => {
    if (!values) return;
    setSaveError(null);
    try {
      await update.mutateAsync(values);
    } catch {
      setSaveError('Some values were invalid — check the fields below.');
    }
  };

  const discard = () => setDraft(original ?? null);
  const resetToDefaults = () => setDraft({ ...DEFAULT_BRANDING, logoStorageKey: null, logoMimeType: null, logoDarkStorageKey: null, logoDarkMimeType: null, faviconStorageKey: null, faviconMimeType: null });

  if (isLoading || !values) return <PageHeader breadcrumb={[{ label: 'Administration', to: '/admin' }, { label: 'Branding' }]} />;

  const ratio = /^#[0-9A-Fa-f]{6}$/.test(values.accentColor) ? contrastWithWhite(values.accentColor) : 0;
  const passesAA = ratio >= 4.5;
  const preset = ACCENT_PRESETS.find((p) => p.hex.toLowerCase() === values.accentColor.toLowerCase());
  const subtextLen = (values.loginSubtext ?? '').length;

  const breadcrumb = [{ label: 'Administration', to: '/admin' }, { label: 'Branding' }];

  return (
    <>
      <PageHeader breadcrumb={breadcrumb} />
      <main className="relative flex flex-col gap-4 overflow-hidden px-8 py-7" style={{ height: 'calc(100vh - 60px)', flex: 'none' }}>
        <div className="flex items-start justify-between gap-6">
          <div className="flex flex-col gap-1">
            <h1 className="text-[22px] font-[650] leading-7 tracking-[-0.01em]">Branding</h1>
            <p className="text-sm leading-5 text-text-muted">How Orion looks to your team — names, logos, accent colour and the sign-in page.</p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={resetToDefaults} className="flex h-9 items-center rounded-control px-3 text-sm font-semibold text-primary">
              Reset to defaults
            </button>
            <a href="/login" target="_blank" rel="noreferrer">
              <Button variant="secondary" size="md">
                Open login page
              </Button>
            </a>
          </div>
        </div>

        {saveError && (
          <div role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm font-medium text-danger">
            {saveError}
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          <div className="flex items-start gap-4">
            <section aria-label="Branding settings" className="flex w-lg shrink-0 flex-col gap-4 rounded-dialog border border-border bg-surface p-5 shadow-card">
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1.5 text-[13px] font-semibold">
                  Product name shown to users
                  <input value={values.productName} onChange={(e) => setField('productName', e.target.value)} className="h-9 rounded-control border border-border-strong bg-surface px-3 text-sm font-normal" />
                </label>
                <label className="flex flex-col gap-1.5 text-[13px] font-semibold">
                  Workspace name
                  <input value={values.workspaceName} onChange={(e) => setField('workspaceName', e.target.value)} className="h-9 rounded-control border border-border-strong bg-surface px-3 text-sm font-normal" />
                </label>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <LogoSlot
                  label="Logo (light)"
                  preview={values.logoStorageKey ? '/api/v1/public/branding/logo' : null}
                  dark={false}
                  uploading={uploadLogo.isPending}
                  onUpload={(f) => uploadLogo.mutate(f)}
                  onRemove={() => removeLogo.mutate()}
                />
                <LogoSlot
                  label="Logo (dark)"
                  preview={values.logoDarkStorageKey ? '/api/v1/public/branding/logo-dark' : null}
                  dark
                  uploading={uploadLogoDark.isPending}
                  onUpload={(f) => uploadLogoDark.mutate(f)}
                  onRemove={() => removeLogoDark.mutate()}
                />
                <div className="flex flex-col gap-1.5">
                  <span className="text-[13px] font-semibold">Favicon</span>
                  <FaviconUploader preview={values.faviconStorageKey ? '/api/v1/public/branding/favicon' : null} onUpload={(f) => uploadFavicon.mutate(f)} uploading={uploadFavicon.isPending} />
                  <span className="text-xs text-text-muted">SVG or PNG, 64×64</span>
                  {values.faviconStorageKey && (
                    <button type="button" onClick={() => removeFavicon.mutate()} className="text-left text-xs font-semibold text-text-muted">
                      Remove
                    </button>
                  )}
                </div>
              </div>

              <div role="radiogroup" aria-label="Accent colour" className="flex flex-col gap-2">
                <span className="text-[13px] font-semibold">Accent colour</span>
                <div className="flex items-center gap-3">
                  {ACCENT_PRESETS.map((p) => {
                    const checked = p.hex.toLowerCase() === values.accentColor.toLowerCase();
                    return (
                      <button
                        key={p.hex}
                        type="button"
                        role="radio"
                        aria-checked={checked}
                        aria-label={p.label}
                        onClick={() => setField('accentColor', p.hex as BrandingSettings['accentColor'])}
                        className="flex size-7 items-center justify-center rounded-full"
                        style={{ background: p.hex, boxShadow: checked ? '0 0 0 2px var(--color-surface), 0 0 0 4px ' + p.hex : undefined }}
                      >
                        {checked && (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="m5 12 5 5 9-10" />
                          </svg>
                        )}
                      </button>
                    );
                  })}
                  <span className="ml-1 text-[13px] text-text-secondary">{preset?.label ?? 'Custom'}</span>
                  <span className="flex-1" />
                  <span className={`flex h-6 items-center gap-1 rounded-full px-2.5 text-xs font-semibold ${passesAA ? 'bg-success-soft text-success' : 'bg-danger-soft text-danger'}`}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="m5 12 5 5 9-10" />
                    </svg>
                    {passesAA ? 'Passes AA' : 'Fails AA'} · {ratio.toFixed(1)}:1
                  </span>
                </div>
                <span className="text-xs text-text-muted">Curated so buttons, links and focus rings stay readable on white.</span>
              </div>

              <label className="flex flex-col gap-1.5 border-t border-divider pt-4 text-[13px] font-semibold">
                <span className="flex items-center gap-1.5">
                  {changedKeys.has('loginHeadline') && <span role="img" aria-label="Edited" className="size-1.5 rounded-full bg-primary" />}
                  Login page headline
                </span>
                <input
                  value={values.loginHeadline ?? ''}
                  onChange={(e) => setField('loginHeadline', e.target.value)}
                  className={`h-9 rounded-control border px-3 text-sm font-normal ${changedKeys.has('loginHeadline') ? 'border-primary shadow-[0_0_0_3px_rgba(46,71,214,0.14)]' : 'border-border-strong'}`}
                />
              </label>
              <label className="flex flex-col gap-1.5 text-[13px] font-semibold">
                <span className="flex items-center justify-between">
                  Login page subtext
                  <span className="text-xs font-normal text-text-muted">{subtextLen} / 160</span>
                </span>
                <textarea
                  rows={2}
                  maxLength={160}
                  value={values.loginSubtext ?? ''}
                  onChange={(e) => setField('loginSubtext', e.target.value)}
                  className="resize-none rounded-control border border-border-strong bg-surface px-3 py-2 text-sm font-normal leading-5"
                />
              </label>

              <div className="grid grid-cols-2 items-end gap-3">
                <label className="flex flex-col gap-1.5 text-[13px] font-semibold">
                  <span className="flex items-center gap-1.5">
                    {changedKeys.has('supportEmail') && <span role="img" aria-label="Edited" className="size-1.5 rounded-full bg-primary" />}
                    Support email
                  </span>
                  <input
                    type="email"
                    value={values.supportEmail ?? ''}
                    onChange={(e) => setField('supportEmail', e.target.value)}
                    className={`h-9 rounded-control border px-3 text-sm font-normal ${changedKeys.has('supportEmail') ? 'border-primary shadow-[0_0_0_3px_rgba(46,71,214,0.14)]' : 'border-border-strong'}`}
                  />
                </label>
                <div className="flex h-9 items-center gap-2.5 rounded-control bg-surface-subtle px-3">
                  <span className="flex-1 text-[13px] font-semibold">Show &ldquo;Powered by {values.productName || 'Orion'}&rdquo;</span>
                  <Switch checked={values.showPoweredBy} onChange={(v) => setField('showPoweredBy', v)} label="Show powered-by" hideLabel />
                </div>
              </div>
            </section>

            <section aria-labelledby="prev-h" className="min-w-0 flex-1 rounded-dialog border border-border bg-surface p-3.5 shadow-card">
              <div className="flex items-center gap-2 px-0.5 pb-3">
                <span aria-hidden className="size-2 rounded-full" style={{ background: 'var(--color-success)' }} />
                <h2 id="prev-h" className="flex-1 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                  Live preview
                </h2>
                <span className="text-xs text-text-muted">Updates as you type</span>
              </div>

              <div role="img" aria-label="Login page preview" className="flex h-75 flex-col items-center justify-center gap-2.5 rounded-control border border-border bg-ground">
                <div className="flex w-58 flex-col gap-2 rounded-input border border-border bg-surface p-4 shadow-card">
                  <div className="flex items-center gap-1.5">
                    <span className="flex size-4.5 items-center justify-center rounded-xs" style={{ background: '#12151b' }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true">
                        <circle cx="6.5" cy="16" r="2" fill="#F2B25C" />
                        <circle cx="12" cy="12" r="2" fill="#F2B25C" />
                        <circle cx="17.5" cy="8" r="2" fill="#F2B25C" />
                      </svg>
                    </span>
                    <span className="text-xs font-[650]">{values.productName || 'Orion'}</span>
                  </div>
                  <span className="mt-0.5 font-sans text-xl leading-5.5">{values.loginHeadline || `Welcome to ${values.workspaceName}`}</span>
                  <span className="text-[10px] leading-3.5 text-text-muted">{values.loginSubtext || 'Sign in to reach your leads, deals and tasks.'}</span>
                  <span className="mt-0.5 flex h-7 items-center justify-center gap-1.5 rounded-[6px] border border-border-strong text-[11px] font-semibold">
                    <MicrosoftLogo />
                    Sign in with Microsoft
                  </span>
                  <span className="flex items-center gap-1.5 text-[9px] text-text-faint">
                    <span className="h-px flex-1 bg-border" />or<span className="h-px flex-1 bg-border" />
                  </span>
                  <span className="flex h-6.5 items-center rounded-[6px] border border-border-strong px-2 text-[10px] text-text-faint">you@company.com</span>
                  <span className="flex h-6.5 items-center justify-center rounded-[6px] text-[11px] font-semibold text-white" style={{ background: /^#[0-9A-Fa-f]{6}$/.test(values.accentColor) ? values.accentColor : '#2E47D6' }}>
                    Continue
                  </span>
                  <span className="text-center text-[9px] text-text-muted">
                    Need help? <span className="font-semibold" style={{ color: values.accentColor }}>{values.supportEmail || 'support@example.com'}</span>
                  </span>
                </div>
                {values.showPoweredBy && <span className="text-[9px] text-text-faint">Powered by {values.productName || 'Orion'}</span>}
              </div>

              <div role="img" aria-label="App preview" className="mt-3 flex h-49 overflow-hidden rounded-control border border-border">
                <div className="flex w-26 shrink-0 flex-col gap-0.75 bg-night px-1.5 py-2.5">
                  <div className="flex items-center gap-1.25 px-1 pb-2">
                    <span className="flex size-3.5 items-center justify-center rounded-[4px] bg-night-surface">
                      <svg width="10" height="10" viewBox="0 0 24 24" aria-hidden="true">
                        <circle cx="6.5" cy="16" r="2.4" fill="#F2B25C" />
                        <circle cx="12" cy="12" r="2.4" fill="#F2B25C" />
                        <circle cx="17.5" cy="8" r="2.4" fill="#F2B25C" />
                      </svg>
                    </span>
                    <span className="flex flex-col leading-[1.1]">
                      <span className="text-[9px] font-[650] text-white">{values.productName || 'Orion'}</span>
                      <span className="text-[7px] text-nav-text-muted">{values.workspaceName}</span>
                    </span>
                  </div>
                  <span className="flex h-4.5 items-center rounded-xs px-1.5 text-[8px] text-nav-text-secondary">Dashboard</span>
                  <span className="flex h-4.5 items-center gap-1 rounded-xs bg-[#262b36] px-1.5 text-[8px] text-white">
                    <span className="size-1 rounded-full" style={{ background: 'var(--color-amber-deep)' }} />
                    Leads
                  </span>
                  <span className="flex h-4.5 items-center rounded-xs px-1.5 text-[8px] text-nav-text-secondary">Contacts</span>
                  <span className="flex h-4.5 items-center rounded-xs px-1.5 text-[8px] text-nav-text-secondary">Opportunities</span>
                  <span className="flex h-4.5 items-center rounded-xs px-1.5 text-[8px] text-nav-text-secondary">Tasks</span>
                  <span className="flex h-4.5 items-center rounded-xs px-1.5 text-[8px] text-nav-text-secondary">Reports</span>
                </div>
                <div className="flex flex-1 flex-col gap-2 bg-ground p-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-[650]">Leads</span>
                    <span className="flex h-4 items-center rounded-xs px-1.5 text-[7px] font-semibold text-white" style={{ background: /^#[0-9A-Fa-f]{6}$/.test(values.accentColor) ? values.accentColor : '#2E47D6' }}>
                      + New lead
                    </span>
                  </div>
                  <div className="flex gap-2.5 border-b border-border text-[7px] font-semibold">
                    <span className="pb-0.75 text-ink" style={{ borderBottom: `1.5px solid ${values.accentColor}` }}>
                      All leads
                    </span>
                    <span className="text-text-muted">My leads</span>
                    <span className="text-text-muted">Hot</span>
                  </div>
                  <div className="flex flex-col rounded-xs border border-border bg-surface">
                    {[
                      { name: 'Jordan Blake', tag: 'Qualified', bg: '#F1ECFD', fg: '#5B34B0' },
                      { name: 'Mei Tanaka', tag: 'New', bg: '#ECEFFD', fg: '#2439B3' },
                      { name: 'Rafael Costa', tag: 'Contacted', bg: '#E3F5F3', fg: '#0F6B63' },
                    ].map((row) => (
                      <div key={row.name} className="flex h-5 items-center gap-1.5 border-b border-divider px-1.5 text-[7px] last:border-b-0">
                        <span className="size-1.75 rounded-xs" style={{ background: values.accentColor }} />
                        <span className="flex-1 font-semibold">{row.name}</span>
                        <span className="rounded-full px-1.25 py-px font-semibold" style={{ background: row.bg, color: row.fg }}>
                          {row.tag}
                        </span>
                      </div>
                    ))}
                  </div>
                  <span className="text-[7px] font-semibold" style={{ color: values.accentColor }}>
                    View all 128 leads →
                  </span>
                </div>
              </div>
            </section>
          </div>
        </div>

        {dirty && (
          <div role="region" aria-label="Unsaved changes" className="absolute inset-x-8 bottom-5 flex h-14 items-center gap-3 rounded-dialog bg-night pl-4.5 pr-2.5 text-white shadow-dialog">
            <span className="size-2 shrink-0 rounded-full" style={{ background: 'var(--color-amber-deep)' }} />
            <span className="text-sm font-semibold">You have unsaved changes</span>
            <span className="truncate text-[13px] text-[#9aa0ab]">
              {changedKeys.size} field{changedKeys.size === 1 ? '' : 's'} · {[...changedKeys].map((k) => FIELD_LABEL[k] ?? k).join(', ')}
            </span>
            <span className="flex-1" />
            <button type="button" onClick={discard} className="flex h-9 items-center rounded-control border border-[#3a404c] px-3.5 text-sm font-semibold text-[#e6e8ec] hover:bg-white/5">
              Discard
            </button>
            <Button size="md" onClick={save} loading={update.isPending}>
              Save changes
            </Button>
          </div>
        )}
      </main>
    </>
  );
}

function FaviconUploader({ preview, onUpload, uploading }: { preview: string | null; onUpload: (file: File) => void; uploading: boolean }) {
  const inputRef = useRef<HTMLInputElement>(null);
  if (preview) {
    return (
      <button type="button" onClick={() => inputRef.current?.click()} className="flex h-16 items-center justify-center rounded-control border border-border bg-surface-subtle">
        <img src={preview} alt="" className="size-8 object-contain" />
        <input ref={inputRef} type="file" accept="image/png,image/svg+xml,image/x-icon" className="hidden" onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])} />
      </button>
    );
  }
  return (
    <button
      type="button"
      disabled={uploading}
      onClick={() => inputRef.current?.click()}
      className="flex h-16 flex-col items-center justify-center gap-1 rounded-control border border-dashed border-border-strong text-xs font-semibold text-primary"
    >
      <input ref={inputRef} type="file" accept="image/png,image/svg+xml,image/x-icon" className="hidden" onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])} />
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 16V4M7 9l5-5 5 5" />
        <path d="M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
      </svg>
      Upload favicon
    </button>
  );
}

function MicrosoftLogo() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="2" y="2" width="9.5" height="9.5" fill="#F25022" />
      <rect x="12.5" y="2" width="9.5" height="9.5" fill="#7FBA00" />
      <rect x="2" y="12.5" width="9.5" height="9.5" fill="#00A4EF" />
      <rect x="12.5" y="12.5" width="9.5" height="9.5" fill="#FFB900" />
    </svg>
  );
}
